import { useEffect, useRef, useState } from 'react';

import { ExportOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import { useSearchArticlesInfiniteInfiniteQuery } from 'services';
import useDebounce from 'hooks/useDebounce';

/** Modal açılış animasyonu boyunca odak denemesi yapılacak azami frame sayısı (~0.5sn) */
const MAX_FOCUS_ATTEMPTS = 30;

/** Her Select örneğine tekil bir input id'si üretmek için kullanılan sayaç. */
let nextInstanceId = 0;

/**
 * @param {Object} props
 * @param {Function} props.onSelect - Madde seçildiğinde çağrılacak callback.
 * @param {boolean} [props.isSelecting=false] - true iken Select loading ve disabled duruma geçer.
 * @param {string} [props.excludeId] - Sonuçlardan çıkarılacak madde. Format: {type}-{id} (ör: tematik-123)
 * @param {boolean} [props.autoFocus=false] - true ise mount olur olmaz arama alanına odaklanır.
 *   Yalnızca modal gibi tek amacı arama olan yerlerde kullanılmalı; sayfa içi kullanımda
 *   otomatik odak scroll'u alana zıplatır.
 */
function SearchArticle({ onSelect, isSelecting = false, excludeId, autoFocus = false }) {
  const [searchText, setSearchText] = useState('');
  const selectRef = useRef(null);
  const [inputId] = useState(() => {
    nextInstanceId += 1;
    return `search-article-${nextInstanceId}`;
  });

  const { data, isFetching, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useSearchArticlesInfiniteInfiniteQuery({ q: searchText, excludeId });
  const { handleDebounce } = useDebounce();

  /**
   * Arama alanına odaklanır.
   *
   * rc-dialog, modal sarmalayıcısını `display: none` ile tutuyor ve bunu ancak
   * açılıştan sonraki render'da kaldırıyor (Dialog `animatedVisible` state'i).
   * Görünmeyen bir elemana `focus()` sessizce başarısız olduğu için tek seferlik
   * bir çağrı kaçabiliyor; bu yüzden odak gerçekten yerleşene kadar her frame'de
   * yeniden deneniyor. (Aynı desen: article-relation-table/note-modal.jsx)
   */
  useEffect(() => {
    if (!autoFocus) return undefined;

    let frame;
    let attemptsLeft = MAX_FOCUS_ATTEMPTS;

    const focusWhenVisible = () => {
      selectRef.current?.focus();

      attemptsLeft -= 1;
      if (document.activeElement?.id === inputId || attemptsLeft <= 0) return;

      frame = requestAnimationFrame(focusWhenVisible);
    };

    frame = requestAnimationFrame(focusWhenVisible);
    return () => cancelAnimationFrame(frame);
  }, [autoFocus, inputId]);

  const pages = data?.pages ?? [];
  const rawArticles = pages.flatMap((page) => page['hydra:member']);

  /**
   * Infinite scroll ile biriken sayfalarda API aynı article'ı birden fazla döndürebilir.
   * antd Select, value'yu React key olarak kullandığından duplicate value'lar
   * virtual list'i bozar. (ref: ant-design#39718)
   * Map ile uid bazında tekil tutuyoruz — aynı uid varsa son gelen korunur.
   */
  const allArticles = Array.from(
    new Map(rawArticles.map((article) => [article.uid, article])).values(),
  );

  const handleSearch = (value) => {
    handleDebounce({
      callback: () => {
        setSearchText(value);
      },
    });
  };

  const handlePopupScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20 && !isFetchingNextPage && hasNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Select
      ref={selectRef}
      id={inputId}
      className="gx-mt-2 gx-w-100"
      placeholder="Madde ara ve ekle"
      loading={isFetching || isSelecting}
      disabled={isSelecting}
      options={allArticles.map((article) => {
        const pillItems = [
          article.type && { label: 'Ansiklopedi', value: article.type.toUpperCase() },
          article.articleGroup && { label: 'Madde Grubu', value: article.articleGroup },
          article.articleType && { label: 'Madde Türü', value: article.articleType },
          article.committee && { label: 'İlim Heyeti', value: article.committee },
          article.lifespan && { label: 'Doğum/Ölüm', value: article.lifespan },
          article.isAbandoned && { value: 'Terk', color: '#1890ff' },
          article.isDraft && { value: 'Taslak', color: '#fa8c16' },
          article.isCancelled && { value: 'İptal', color: '#f5222d' },
        ].filter(Boolean);

        const textItems = [
          article.lead && { label: 'Takdim', value: article.lead },
          article.kunya && { label: 'Künye', value: article.kunya },
        ].filter(Boolean);

        return {
          label: (
            <div>
              <div className="gx-d-flex gx-align-items-center" style={{ gap: 6 }}>
                <span>{article.title}</span>
                {article.resourceUrl && (
                  <a
                    href={article.resourceUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExportOutlined style={{ fontSize: 14, color: '#1890ff' }} />
                  </a>
                )}
              </div>
              {textItems.map((item, index) => (
                <div key={index} style={{ fontSize: 11, color: '#888', marginTop: 2, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  <strong>{item.label}:</strong> {item.value}
                </div>
              ))}
              {pillItems.length > 0 && (
                <div className="gx-d-flex" style={{ flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                  {pillItems.map((item, index) => (
                    <span
                      key={index}
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 4,
                        backgroundColor: item.color ? `${item.color}10` : '#f5f5f5',
                        color: item.color ?? '#888',
                        border: `1px solid ${item.color ? `${item.color}30` : '#e8e8e8'}`,
                        lineHeight: '18px',
                      }}
                    >
                      {item.label ? <><strong>{item.label}:</strong> {item.value}</> : item.value}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ),
          value: article.uid,
          entryId: article.entryId,
          article,
        };
      })}
      onSelect={(_, option) => onSelect(option)}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      listHeight={500}
      filterOption={false}
      showSearch
    />
  );
}

export default SearchArticle;
